import { openOAuthConnectPopup } from '../oauth-connect-lifecycle'
import { FC, useEffect, useState } from 'react';
import { queryClient, trpc, trpcClient } from '@/lib/trpc';
import { Integration, IntegrationFeature, IntegrationProvider, ConfigModalProps } from '../types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog';
import { Button } from '@deepguide-ai/ui/button';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Jira icon wrapper component
 */
const JiraIconWrapper: FC = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <linearGradient id="jiraGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#2684FF" />
        <stop offset="100%" stopColor="#0052CC" />
      </linearGradient>
    </defs>
    <path
      d="M11.53.02C11.53 4.4 7.92 7.99 3.51 7.99H.04v.04C.04 12.42 3.65 16.02 8.05 16.02h3.48V24l.01-.01c0-4.4 3.62-7.99 8.02-7.99h3.48V8.01h-3.48c-4.4 0-8.02-3.61-8.03-7.99V.02zm0 8.01h3.48v7.98H11.5l.03.01z"
      fill="url(#jiraGradient)"
    />
  </svg>
);

/**
 * Jira config modal component
 */
const JiraConfigModal: FC<ConfigModalProps> = ({
  open,
  onOpenChange,
  workspaceId,
  onComplete,
  integration,
}) => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [awaitingAuthResult, setAwaitingAuthResult] = useState(false);

  useEffect(() => {
    if (!open) {
      setIsConnecting(false);
      setAwaitingAuthResult(false);
    }
  }, [open]);

  useEffect(() => {
    if (open && awaitingAuthResult && integration?.id) {
      setIsConnecting(false);
      setAwaitingAuthResult(false);
      onComplete();
    }
  }, [awaitingAuthResult, integration?.id, onComplete, open]);

  const handleConnect = async () => {
    setIsConnecting(true);
    setAwaitingAuthResult(true);
    try {
      await JiraProvider.handleConnect({
        workspaceId,
        onSuccess: () => {
          setIsConnecting(false);
          setAwaitingAuthResult(false);
          onComplete();
        },
        onFinish: (status) => {
          if (status === 'error') {
            setIsConnecting(false);
            setAwaitingAuthResult(false);
          } else if (status === 'closed') {
            setIsConnecting(false);
          }
        },
      });
    } catch (error) {
      console.error('Failed to connect to Jira:', error);
      setIsConnecting(false);
      setAwaitingAuthResult(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Connect to Jira</DialogTitle>
          <DialogDescription>
            Connect your Jira account to import issues and project documentation into your workspace.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 p-4">
          <div className="text-sm text-muted-foreground">
            You'll be redirected to Jira to authorize the connection. Make sure pop-ups are enabled for this site.
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isConnecting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConnect}
              disabled={isConnecting}
            >
              {isConnecting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Connecting...
                </>
              ) : (
                'Connect to Jira'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Jira integration provider
 */
export const JiraProvider: IntegrationProvider = {
  id: 'Jira',
  name: 'Jira',
  description: 'Connect to Jira to import issues and projects',
  Icon: JiraIconWrapper,
  ConfigModal: JiraConfigModal,

  getFeatures: (): IntegrationFeature[] => [
    {
      id: 'issues',
      name: 'Issues',
      description: 'Import Jira issues',
      enabled: true,
    },
    {
      id: 'projects',
      name: 'Projects',
      description: 'Import Jira projects',
      enabled: false,
    },
  ],

  handleConnect: async ({ workspaceId, features, redirectUrl, onSuccess, onFinish }) => {
    const targetRedirectUrl =
      redirectUrl || `${window.location.origin}/auth-callback/Jira`;

    const data = await trpcClient.integrations.initJiraAuth.mutate({
      workspaceId,
      features,
      redirectUrl: targetRedirectUrl,
      providerType: 'Jira',
    });

    if (!data.authUrl) {
      console.error('No Jira auth URL returned from initialization');
      return;
    }

    openOAuthConnectPopup({
      authUrl: data.authUrl, windowName: 'jira-auth', provider: 'Jira',
      onSuccess: result => {
        void queryClient.invalidateQueries({ queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey() });
        onSuccess?.(result);
      },
      onError: message => toast.error(message),
      onPopupBlocked: () => toast.error('OAuth popup was blocked. Allow popups and try again.'),
      onFinish: onFinish,
    })
  },

  onConnectComplete: async ({ code, state }) => {
    const result = await trpcClient.integrations.completeJiraAuth.mutate({
      code,
      state,
    });

    queryClient.invalidateQueries({
      queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey(),
    });

    return result;
  },

  syncData: async () => {
    await trpcClient.tickets.ingest.mutate({
      platforms: ['JIRA'],
    });

    queryClient.invalidateQueries({
      predicate: ({ queryKey }) =>
        Array.isArray(queryKey) && queryKey[0] === 'tickets.list',
    });

    queryClient.invalidateQueries({
      queryKey: trpc.tickets.getIngestionStatus.queryKey({}),
    });
  },

  shouldDisplay: () => true,
};
